<template>
    <app-layout :title="$t('View user')">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ $t('View user') }}
            </h2>
        </template>


        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <page-heading :breadcrumbs="breadcrumbs" class="px-4 sm:px-0">
                <template #title>
                    {{ user.name }}
                </template>
                <template #actionButtons>
                    <jet-secondary-button type="button" @click.prevent="resendCustomerWelcomeMail">
                        {{ $t('Send welcome message') }}
                    </jet-secondary-button>
                </template>
            </page-heading>

            <div class="py-5">
                <div class="sm:hidden">
                    <label for="tabs" class="sr-only">{{ $t('Select tab') }}</label>
                    <!-- Use an "onChange" listener to redirect the user to the selected tab URL. -->
                    <select id="tabs" name="tabs"
                            class="block w-full focus:ring-primary-500 focus:border-primary-500 border-gray-300 rounded-md"
                            v-model="selectedTab">
                        <option v-for="tab in filteredTabs" :key="tab.id" :value="tab.id">{{
                                $t(tab.name)
                            }}
                        </option>
                    </select>
                </div>
                <div class="hidden sm:block">
                    <nav class="relative z-0 rounded-lg shadow flex divide-x divide-gray-200" :aria-label="$t('Tabs')">
                        <a v-for="(tab, tabIdx) in filteredTabs" :key="tab.id" :href="tab.href"
                           :class="[selectedTab == tab.id ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700', tabIdx === 0 ? 'rounded-l-lg' : '', tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '', 'group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10']"
                           :aria-current="selectedTab == tab.id ? 'page' : undefined" @click.prevent="changeTab(tab)">
                            <span>{{ $t(tab.name) }}</span>
                            <span aria-hidden="true"
                                  :class="[selectedTab == tab.id ? 'bg-primary-500' : 'bg-transparent', 'absolute inset-x-0 bottom-0 h-0.5']"/>
                        </a>
                    </nav>
                </div>
            </div>

            <div class="py-3">
                <template v-if="selectedTab == 'master_data'">
                    <UserMasterDataManager :user="user"></UserMasterDataManager>
                </template>

                <template v-if="selectedTab == 'workspaces'">
                    <div class="flex flex-col mb-3">
                        <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                <div class="mb-6 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-gray-50">
                                        <tr>
                                            <th scope="col"
                                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                #
                                            </th>
                                            <th scope="col"
                                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                {{ $t('Name') }}
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody class="bg-white divide-y divide-gray-200">
                                        <template v-if="user.workspaces && user.workspaces.length > 0">
                                            <tr v-for="(workspace, idx) in user.workspaces">
                                                <td class="px-6 py-4 whitespace-nowrap">
                                                    <div class="flex items-center">
                                                        <div class="text-sm font-medium text-gray-400">
                                                            {{ workspace.internal_uid }}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="px-6 py-4 whitespace-nowrap">
                                                    <div class="flex items-center">
                                                        <div class="text-sm font-medium text-gray-900">
                                                            <div class="text-lg text-primary-500">
                                                                <Link :href="workspace.show_url" target="_blank"
                                                                      class="text-primary-600 hover:text-primary-900">
                                                                    {{ workspace.name }}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        <template v-else>
                                            <tr>
                                                <td colspan="2" class="px-6 py-4 whitespace-nowrap text-center">
                                                    {{ $t('No data available.') }}
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                                <AlertBox type="info">
                                    <div class="flex-1 md:flex md:justify-between">
                                        <p>
                                            {{ $t('User assignment to a workspace must be done through the respective workspace.') }}
                                        </p>
                                    </div>
                                </AlertBox>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from "@/Components/Badge";
import PageHeading from "@/Components/PageHeading";
import UserMasterDataManager from "@/Components/UserProfile/UserMasterDataManager";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import {MinusCircleIcon} from "@heroicons/vue/solid";
import {Link} from "@inertiajs/vue3";
import AlertBox from "../../Components/AlertBox.vue";

export default defineComponent({
    components: {
        AlertBox,
        MinusCircleIcon,
        AppLayout,
        PageHeading,
        JetSecondaryButton,
        Link,
        Badge,
        UserMasterDataManager,
    },

    computed: {
        filteredTabs() {
            return this.tabs;
        }
    },

    props: {
        user: Object,
    },

    data() {
        return {
            tabs: [
                {name: 'Master data', id: 'master_data', href: '#'},
                {name: 'Workspaces', id: 'workspaces', href: '#'},
            ],
            selectedTab: 'master_data',
            breadcrumbs: [
                {
                    label: 'Users',
                    url: route('users.index'),
                },
                {
                    label: this.user.name,
                    url: route('users.show', {user: this.user.id}),
                }
            ]
        };
    },

    methods: {
        changeTab(tab) {
            this.selectedTab = tab.id;
        },
        resendCustomerWelcomeMail() {
            this.$inertia.post(
                route('user.welcome-mail.send'),
                {user_id: this.user.id},
                {
                    preserveScroll: true,
                    preserveState: false,
                    onSuccess: () => {

                    },
                }
            );
        },
    }
})
</script>
